import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { POST } from '../../services/rest.service';
import { showModal } from '../../redux/error/ErrorAction';
import { showDocumentModal } from '../../redux/user/UserActions';
import { hideLoading, showLoading } from '../../redux/loader/LoaderActions';
import { acceptedLadingTypes } from '../../helpers/constants';
import { fetchPrivateDocument, downloadPrivateDocument, ladingFileProblem } from '../../helpers/utils';

/**
 * The Bill of Lading and Tax Identification Number a job needs after a bid is
 * accepted.
 *
 * Presence-driven, like AgentInfo: it renders whatever the API decided this
 * viewer may have and holds no role logic of its own. `docs.can_view` is the
 * server's answer on whether the document itself may be opened; `editable`
 * says whether this screen is the importer's, since only they (or staff) may
 * supply the documents.
 *
 * Until both arrive the clearing agent cannot record any milestone, which is
 * why the wording here is about unblocking the agent rather than about
 * paperwork.
 *
 * @param {object}   docs      details.privateJobDocs
 * @param {string}   jobId     job uuid
 * @param {boolean}  editable  show the upload form
 * @param {function} refresh   re-fetch the job after a successful submit
 * @param {string}   idSuffix  keeps element ids unique when a second copy is
 *                             mounted, as the post-award pop-up does
 */
const PrivateJobDocuments = ({ docs, jobId, editable = false, refresh, idSuffix = '' }) => {
  const dispatch = useDispatch();
  const [tin, setTin] = useState('');
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const tinId = `post_award_tin${idSuffix ? `_${idSuffix}` : ''}`;
  const ladingId = `post_award_lading${idSuffix ? `_${idSuffix}` : ''}`;

  if (!docs) return null;

  // Telex Release means the shipping line released the cargo electronically.
  // There is no document to produce, so the file input is hidden entirely
  // rather than shown and ignored.
  const isTelex = !!docs.is_telex;
  const complete = !!docs.post_award_docs_complete;
  const needsLading = !isTelex && !docs.has_lading;
  const needsTin = !docs.has_tin;

  const openDocument = async () => {
    const { src, message } = await fetchPrivateDocument(docs.lading_url, docs.lading_is_legacy);
    if (src) {
      dispatch(showDocumentModal(src));
    } else {
      dispatch(showModal(message || 'This document could not be opened.'));
    }
  };

  const saveDocument = async () => {
    const { ok, message } = await downloadPrivateDocument(docs);
    if (!ok) {
      dispatch(showModal(message || 'This document could not be downloaded.'));
    }
  };

  const validate = () => {
    const e = {};
    if (needsTin && !String(tin).trim()) {
      e.tin = 'Tax Identification Number is required';
    } else if (String(tin).trim() && !/^[0-9]{8,15}$/.test(String(tin).trim())) {
      e.tin = 'Enter 8 to 15 digits';
    }
    if (needsLading && !file) {
      e.file = 'Bill of Lading is required';
    } else if (file && ladingFileProblem(file)) {
      e.file = ladingFileProblem(file);
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (ev) => {
    ev.preventDefault();
    if (!validate()) return;

    // FormData because of the file; axios sets the multipart boundary itself.
    const payload = new FormData();
    payload.append('job_uuid', jobId);
    if (String(tin).trim()) payload.append('tax_identification_no', String(tin).trim());
    if (file) payload.append('lading_link', file);

    dispatch(showLoading());
    POST('/jobs/uploadPostAwardDocuments', payload)
      .then(() => {
        dispatch(hideLoading());
        dispatch(showModal('Documents submitted successfully', true));
        setFile(null);
        setErrors({});
        if (refresh) refresh();
      })
      .catch((err) => {
        dispatch(hideLoading());
        dispatch(showModal(err?.message || 'Could not submit these documents.'));
      });
  };

  return (
    <div className="card mt-4">
      <div className="card-body">
        <h5 className="mb-3">Clearing Documents</h5>

        {complete ? (
          <p className="text-success mb-3">
            <i className="fa fa-check-circle me-2"></i>
            {editable
              ? 'All required documents have been supplied. Your clearing agent can proceed.'
              : 'All required documents have been supplied.'}
          </p>
        ) : (
          <p className="text-danger mb-3">
            {editable
              ? 'Your clearing agent cannot begin work on this job until the documents below are supplied.'
              : 'Milestones cannot be recorded on this job until the importer supplies the documents below.'}
          </p>
        )}

        <div className="row mb-3">
          <div className="col-lg-6 col-sm-12">
            <p className="mb-1">
              <strong>Tax Identification Number:</strong>{' '}
              {docs.has_tin ? docs.tax_identification_no || 'Provided' : 'Not provided'}
            </p>
          </div>
          <div className="col-lg-6 col-sm-12">
            <p className="mb-1">
              <strong>Bill of Lading:</strong>{' '}
              {isTelex ? (
                'Not required (Telex Release)'
              ) : docs.has_lading ? (
                docs.can_view ? (
                  <>
                    <span
                      className="text-primary"
                      style={{ cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={openDocument}
                    >
                      View
                    </span>
                    <span className="text-muted mx-2">|</span>
                    <span
                      className="text-primary"
                      style={{ cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={saveDocument}
                    >
                      <i className="fa fa-download me-1"></i>
                      Download
                    </span>
                  </>
                ) : (
                  // The server decided this viewer may not have the file. It
                  // still says one exists, because knowing the job is unblocked
                  // is not the same as reading the document.
                  'Provided'
                )
              ) : (
                'Not provided'
              )}
            </p>
          </div>
        </div>

        {editable && !complete && (
          <form onSubmit={handleSubmit}>
            <div className="row">
              {needsTin && (
                <div className="col-lg-6 col-sm-12 mb-3">
                  <label htmlFor={tinId}>
                    Tax Identification Number <span className="text-danger">*</span>
                  </label>
                  {/* Digits only, and numeric keypad on a phone. Not
                      type="number", which drops leading zeros and accepts
                      "e", "+" and "-" — none of which belong in a TIN. */}
                  <input
                    id={tinId}
                    name={tinId}
                    type="text"
                    inputMode="numeric"
                    maxLength={15}
                    placeholder="8 to 15 digits"
                    className={`form-control ${errors.tin ? 'is-invalid' : ''}`}
                    value={tin}
                    onChange={(e) => setTin(e.target.value.replace(/\D/g, ''))}
                  />
                  {errors.tin && <div className="invalid-feedback d-block">{errors.tin}</div>}
                </div>
              )}

              {/* Hidden under Telex Release — there is no document. */}
              {needsLading && (
                <div className="col-lg-6 col-sm-12 mb-3">
                  <label htmlFor={ladingId}>
                    Bill of Lading <span className="text-danger">*</span>
                  </label>
                  <input
                    id={ladingId}
                    name={ladingId}
                    type="file"
                    accept={acceptedLadingTypes}
                    className={`form-control ${errors.file ? 'is-invalid' : ''}`}
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                  />
                  {errors.file && <div className="invalid-feedback d-block">{errors.file}</div>}
                  <small className="text-muted">PDF or photo (JPG, PNG), up to 10MB.</small>
                </div>
              )}
            </div>

            <button type="submit" className="btn btn-primary btn-sm">
              Submit Documents
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default PrivateJobDocuments;
